import { Capacitor } from "@capacitor/core";
import { useState, useCallback, useEffect, useRef } from "react";

import { api } from "../lib/api";
import {
  clearCachedPushToken,
  getCachedPushToken,
  getInstallId,
  isOptedOut,
  markResynced,
  setCachedPushToken,
  setOptedOut,
  shouldResync,
} from "../lib/pushStorage";
import { useAuthStore } from "../store/authStore";

type PermissionState = "default" | "granted" | "denied" | "unsupported";

interface PushSubscription {
  id: string;
  endpoint: string;
  deviceName?: string;
  platform?: string;
  createdAt: string;
  lastUsedAt?: string;
}

interface NotificationStats {
  unreadCount: number;
  totalCount: number;
  pushEnabled: boolean;
}

interface UsePushNotificationsResult {
  // State
  isSupported: boolean;
  permission: PermissionState;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  subscriptions: PushSubscription[];
  stats: NotificationStats | null;

  // Actions
  requestPermission: () => Promise<boolean>;
  subscribe: (deviceName?: string) => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  refreshStats: () => Promise<void>;
  refreshSubscriptions: () => Promise<void>;
}

// Detect if running inside a Capacitor native shell
const isNative = Capacitor.isNativePlatform();
const nativePlatform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

// Custom event for same-page token changes
const NATIVE_TOKEN_EVENT = "native-push-token-changed";

// Key for pending navigation from notification tap (survives app restart)
const PENDING_NAV_KEY = "push_pending_nav";

// The "registration" listener must be attached exactly once, and it must exist
// before register() is called. Both the auto-prompt and the settings toggle go
// through here so neither depends on the other having mounted first.
let nativeListenersAttached = false;

async function ensureNativeRegistration(): Promise<void> {
  const { PushNotifications } = await import("@capacitor/push-notifications");

  if (!nativeListenersAttached) {
    nativeListenersAttached = true;

    await PushNotifications.addListener("registration", async token => {
      setCachedPushToken(token.value);
      window.dispatchEvent(
        new CustomEvent(NATIVE_TOKEN_EVENT, { detail: token.value }),
      );

      try {
        await api.post("/api/notifications/push/subscribe-native", {
          deviceToken: token.value,
          platform: nativePlatform as "ios" | "android",
          deviceName: getDeviceName(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          installId: getInstallId(),
        });
        markResynced();
        console.log("[Push/Native] Token registered with backend");
      } catch (err) {
        console.error("[Push/Native] Failed to register token:", err);
      }
    });

    await PushNotifications.addListener("registrationError", err => {
      console.error("[Push/Native] Registration error:", err);
    });
  }

  // Always re-register. The OS hands back the same token, the POST is an
  // idempotent upsert, and it re-arms a row the backend had deactivated —
  // which is the only way a device that went silent can recover.
  await PushNotifications.register();
}

/**
 * Re-send an existing browser subscription so a deactivated row comes back.
 * The web client otherwise reads `getSubscription()` purely for local UI state
 * and never tells the backend the subscription is still alive.
 */
async function resyncWebSubscription(
  registration: ServiceWorkerRegistration,
): Promise<void> {
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  const p256dh = subscription.getKey("p256dh");
  const auth = subscription.getKey("auth");
  if (!p256dh || !auth) return;

  await api.post("/api/notifications/push/subscribe", {
    subscription: {
      endpoint: subscription.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dh))),
        auth: btoa(String.fromCharCode(...new Uint8Array(auth))),
      },
    },
    userAgent: navigator.userAgent,
    deviceName: getDeviceName(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  markResynced();
}

// Convert base64 string to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications(): UsePushNotificationsResult {
  const [permission, setPermission] = useState<PermissionState>("default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscriptions, setSubscriptions] = useState<PushSubscription[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [swRegistration, setSwRegistration] =
    useState<ServiceWorkerRegistration | null>(null);
  const nativeTokenRef = useRef<string | null>(null);

  // Supported if native OR if web push APIs available
  const isSupported =
    isNative ||
    (typeof window !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window);

  // ─── Native (Capacitor) initialization — sync state ───
  useEffect(() => {
    if (!isNative) return;

    const initNative = async () => {
      try {
        const { PushNotifications } =
          await import("@capacitor/push-notifications");

        // Check current permission state
        const permResult = await PushNotifications.checkPermissions();
        if (permResult.receive === "granted") {
          setPermission("granted");
        } else if (permResult.receive === "denied") {
          setPermission("denied");
        } else {
          setPermission("default");
        }

        // Check if we have a stored token (meaning we're subscribed)
        const storedToken = getCachedPushToken();
        if (storedToken) {
          nativeTokenRef.current = storedToken;
          setIsSubscribed(true);
        }
      } catch (err) {
        console.error("[Push/Native] Failed to check state:", err);
      }
    };

    initNative();

    // Listen for token changes from the auto-prompt (runs in App.tsx)
    const onTokenChanged = (e: Event) => {
      const token = (e as CustomEvent).detail;
      if (token) {
        nativeTokenRef.current = token;
        setIsSubscribed(true);
        setPermission("granted");
      } else {
        nativeTokenRef.current = null;
        setIsSubscribed(false);
      }
    };
    window.addEventListener(NATIVE_TOKEN_EVENT, onTokenChanged);
    return () => window.removeEventListener(NATIVE_TOKEN_EVENT, onTokenChanged);
  }, []);

  // ─── Web initialization ───
  useEffect(() => {
    if (isNative || !isSupported) {
      // oxlint-disable-next-line react/react-compiler -- effect's job is to reflect platform capability into state
      if (!isNative) setPermission("unsupported");
      return;
    }

    const init = async () => {
      try {
        // Check current permission
        setPermission(Notification.permission as PermissionState);

        // Register service worker
        const registration = await navigator.serviceWorker.register("/sw.js", {
          scope: "/",
        });
        console.log("[Push] Service worker registered:", registration.scope);
        setSwRegistration(registration);

        // Check if already subscribed
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);

        // Re-send it so a row the backend deactivated comes back.
        if (subscription && !isOptedOut() && shouldResync()) {
          await resyncWebSubscription(registration);
        }
      } catch (err) {
        console.error("[Push] Failed to initialize:", err);
        setError("Failed to initialize push notifications");
      }
    };

    init();

    // The service worker re-subscribed after the browser rotated our endpoint.
    // Send the new one up immediately — the throttle must not delay this.
    const swContainer = navigator.serviceWorker;
    const onSwMessage = (event: MessageEvent) => {
      if (event.data?.type !== "PUSH_SUBSCRIPTION_CHANGED") return;
      if (isOptedOut()) return;

      swContainer.ready
        .then(resyncWebSubscription)
        .catch(err => console.error("[Push] Endpoint rotation resync:", err));
    };

    swContainer.addEventListener("message", onSwMessage);
    return () => swContainer.removeEventListener("message", onSwMessage);
  }, [isSupported]);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError("Push notifications are not supported");
      return false;
    }

    try {
      if (isNative) {
        const { PushNotifications } =
          await import("@capacitor/push-notifications");
        const result = await PushNotifications.requestPermissions();
        const granted = result.receive === "granted";
        setPermission(granted ? "granted" : "denied");
        return granted;
      } else {
        const result = await Notification.requestPermission();
        setPermission(result as PermissionState);
        return result === "granted";
      }
    } catch (err) {
      console.error("[Push] Failed to request permission:", err);
      setError("Failed to request notification permission");
      return false;
    }
  }, [isSupported]);

  // Native subscribe helper
  const subscribeNative = useCallback(async (): Promise<boolean> => {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    // Request permission if not granted
    const permResult = await PushNotifications.checkPermissions();
    if (permResult.receive !== "granted") {
      const reqResult = await PushNotifications.requestPermissions();
      if (reqResult.receive !== "granted") {
        setPermission("denied");
        setError("Notification permission denied");
        return false;
      }
      setPermission("granted");
    }

    setOptedOut(false);

    // Attaches the 'registration' listener if needed, then registers. The
    // listener is what POSTs the token to the backend.
    await ensureNativeRegistration();
    setIsSubscribed(true);

    return true;
  }, []);

  // Web subscribe helper
  const subscribeWeb = useCallback(
    async (deviceName?: string): Promise<boolean> => {
      if (!swRegistration) {
        setError("Service worker not ready");
        return false;
      }

      // Check browser permission directly to avoid stale closure issues
      const currentPermission = Notification.permission;
      if (currentPermission !== "granted") {
        const granted = await requestPermission();
        if (!granted) {
          setError("Notification permission denied");
          return false;
        }
      }

      setOptedOut(false);

      // Get VAPID public key from backend
      const { publicKey } = await api.get<{ publicKey: string }>(
        "/api/notifications/push/vapid-key",
      );

      if (!publicKey) {
        throw new Error("VAPID public key not configured on server");
      }

      // Subscribe to push manager
      const applicationServerKey = urlBase64ToUint8Array(publicKey);
      const subscription = await swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey.buffer as ArrayBuffer,
      });

      console.log("[Push] Subscribed:", subscription.endpoint);

      // Get subscription keys
      const p256dh = subscription.getKey("p256dh");
      const auth = subscription.getKey("auth");

      if (!p256dh || !auth) {
        throw new Error("Failed to get subscription keys");
      }

      // Convert keys to base64
      const p256dhBase64 = btoa(String.fromCharCode(...new Uint8Array(p256dh)));
      const authBase64 = btoa(String.fromCharCode(...new Uint8Array(auth)));

      // Save subscription to backend
      await api.post("/api/notifications/push/subscribe", {
        subscription: {
          endpoint: subscription.endpoint,
          keys: { p256dh: p256dhBase64, auth: authBase64 },
        },
        userAgent: navigator.userAgent,
        deviceName: deviceName || getDeviceName(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      markResynced();
      setIsSubscribed(true);
      console.log("[Push] Subscription saved to backend");
      return true;
    },
    [swRegistration, requestPermission],
  );

  // Subscribe to push notifications
  const subscribe = useCallback(
    async (deviceName?: string): Promise<boolean> => {
      if (!isSupported) {
        setError("Push notifications are not available");
        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        if (isNative) {
          return await subscribeNative();
        } else {
          return await subscribeWeb(deviceName);
        }
      } catch (err) {
        console.error("[Push] Failed to subscribe:", err);
        setError(
          (err as Error).message || "Failed to subscribe to push notifications",
        );
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [isSupported, subscribeNative, subscribeWeb],
  );

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    // Remembered across launches so the foreground re-register respects it.
    setOptedOut(true);

    try {
      if (isNative) {
        // Unregister native token from backend
        const token = nativeTokenRef.current || getCachedPushToken();
        if (token) {
          await api.post("/api/notifications/push/unsubscribe-native", {
            deviceToken: token,
          });
          clearCachedPushToken();
          nativeTokenRef.current = null;
        }
        setIsSubscribed(false);
        console.log("[Push/Native] Unsubscribed");
        return true;
      } else {
        if (!swRegistration) return false;

        const subscription = await swRegistration.pushManager.getSubscription();
        if (subscription) {
          const endpoint = subscription.endpoint;
          await subscription.unsubscribe();
          await api.post("/api/notifications/push/unsubscribe", { endpoint });
          console.log("[Push] Unsubscribed");
        }

        setIsSubscribed(false);
        return true;
      }
    } catch (err) {
      console.error("[Push] Failed to unsubscribe:", err);
      setError(
        (err as Error).message ||
          "Failed to unsubscribe from push notifications",
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [swRegistration]);

  // Refresh notification stats
  const refreshStats = useCallback(async (): Promise<void> => {
    try {
      const result = await api.get<NotificationStats>(
        "/api/notifications/stats",
      );
      setStats(result);
    } catch (err) {
      console.error("[Push] Failed to get stats:", err);
    }
  }, []);

  // Refresh push subscriptions list
  const refreshSubscriptions = useCallback(async (): Promise<void> => {
    try {
      const { subscriptions: subs } = await api.get<{
        subscriptions: PushSubscription[];
      }>("/api/notifications/push/subscriptions");
      setSubscriptions(subs || []);
    } catch (err) {
      console.error("[Push] Failed to get subscriptions:", err);
    }
  }, []);

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    subscriptions,
    stats,
    requestPermission,
    subscribe,
    unsubscribe,
    refreshStats,
    refreshSubscriptions,
  };
}

/**
 * Handle native push notification taps and foreground delivery.
 * Must be called early at app startup — does NOT require auth.
 *
 * Navigation strategy:
 * - Foreground tap: user is authed, navigate immediately via window.location
 * - Cold start tap: store URL in sessionStorage, usePushPendingNav picks it up after auth
 */
export function useNativePushHandler() {
  useEffect(() => {
    if (!isNative) return;

    let cleanup: (() => void) | undefined;

    const setup = async () => {
      const { PushNotifications } =
        await import("@capacitor/push-notifications");

      const recvListener = await PushNotifications.addListener(
        "pushNotificationReceived",
        notification => {
          console.log("[Push/Handler] Foreground notification:", notification);
        },
      );

      const actionListener = await PushNotifications.addListener(
        "pushNotificationActionPerformed",
        action => {
          console.log("[Push/Handler] Notification tapped:", action);
          const data = action.notification.data;
          if (data?.actionUrl) {
            // Always store for cold start pickup
            sessionStorage.setItem(PENDING_NAV_KEY, data.actionUrl);
            // If user is already authenticated, navigate immediately
            const { user } = useAuthStore.getState();
            if (user) {
              console.log(
                "[Push/Handler] User authed, navigating to:",
                data.actionUrl,
              );
              window.location.assign(data.actionUrl);
            }
          }
        },
      );

      cleanup = () => {
        recvListener.remove();
        actionListener.remove();
      };
    };

    setup();

    return () => {
      cleanup?.();
    };
  }, []);
}

/**
 * Check for pending push notification navigation after auth resolves.
 * Handles cold start: notification tap stored URL before auth was ready.
 * Call this in App.tsx — no router context needed.
 */
export function usePushPendingNav(isAuthenticated: boolean) {
  useEffect(() => {
    if (!isNative || !isAuthenticated) return;

    const pendingUrl = sessionStorage.getItem(PENDING_NAV_KEY);
    if (pendingUrl) {
      sessionStorage.removeItem(PENDING_NAV_KEY);
      console.log("[Push/Nav] Cold start nav to:", pendingUrl);
      window.location.assign(pendingUrl);
    }
  }, [isAuthenticated]);
}

/**
 * Auto-prompt for native push on first launch after login, and re-register on
 * every foreground thereafter.
 *
 * The re-registration is not busywork. The backend deactivates a subscription
 * whose token APNs or FCM has rejected, and registering is the only thing that
 * brings it back. This hook used to skip `register()` whenever a token was
 * already cached, so a device that went silent stayed silent forever.
 */
export function useNativePushAutoPrompt(isAuthenticated: boolean) {
  useEffect(() => {
    if (!isNative || !isAuthenticated) return;

    let removeResumeListener: (() => void) | undefined;

    const registerIfPermitted = async (force: boolean) => {
      if (isOptedOut()) return;

      try {
        const { PushNotifications } =
          await import("@capacitor/push-notifications");

        const permResult = await PushNotifications.checkPermissions();

        if (permResult.receive === "prompt") {
          const reqResult = await PushNotifications.requestPermissions();
          if (reqResult.receive !== "granted") return;
        } else if (permResult.receive !== "granted") {
          return;
        }

        if (!force && !shouldResync()) return;

        await ensureNativeRegistration();
      } catch (err) {
        console.error("[Push/AutoPrompt] Failed:", err);
      }
    };

    // Always register on mount; the throttle only governs later foregrounds.
    registerIfPermitted(true);

    const setupResume = async () => {
      const { App: CapacitorApp } = await import("@capacitor/app");
      const listener = await CapacitorApp.addListener(
        "appStateChange",
        ({ isActive }) => {
          if (isActive) registerIfPermitted(false);
        },
      );
      removeResumeListener = () => listener.remove();
    };

    setupResume();

    return () => {
      removeResumeListener?.();
    };
  }, [isAuthenticated]);
}

// Key tracking whether we've already auto-prompted for web push.
// Browsers consider repeated permission prompts abusive, so we only auto-ask once
// per device — after that, the user can always opt in via NotificationSettings.
const WEB_AUTO_PROMPTED_KEY = "web_push_auto_prompted";

/**
 * Imperative web push prompt — call from a user gesture (e.g. onboarding completion).
 *
 * Browsers require a user gesture for `Notification.requestPermission()` to be reliable
 * and treat repeat prompts as abusive, so we only ever prompt once per device
 * (tracked in localStorage). No-ops on native, when unsupported, or when the user has
 * already decided. Safe to call without awaiting — failures are logged, not thrown.
 */
export async function triggerWebPushPrompt(): Promise<void> {
  if (isNative) return;
  if (typeof window === "undefined") return;
  if (
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    !("Notification" in window)
  ) {
    return;
  }

  if (localStorage.getItem(WEB_AUTO_PROMPTED_KEY)) return;
  if (Notification.permission !== "default") return;

  try {
    // Mark as prompted up front so we never double-ask, even if anything below throws
    localStorage.setItem(WEB_AUTO_PROMPTED_KEY, "1");

    const result = await Notification.requestPermission();
    if (result !== "granted") return;

    const registration = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
    });

    // Already subscribed (e.g. previous session). Don't re-subscribe, but do
    // re-send it — the backend row may have been deactivated since.
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      await resyncWebSubscription(registration);
      return;
    }

    const { publicKey } = await api.get<{ publicKey: string }>(
      "/api/notifications/push/vapid-key",
    );
    if (!publicKey) {
      console.warn("[Push/WebPrompt] No VAPID key configured");
      return;
    }

    const applicationServerKey = urlBase64ToUint8Array(publicKey);
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey.buffer as ArrayBuffer,
    });

    const p256dh = subscription.getKey("p256dh");
    const auth = subscription.getKey("auth");
    if (!p256dh || !auth) {
      console.error("[Push/WebPrompt] Missing subscription keys");
      return;
    }

    const p256dhBase64 = btoa(String.fromCharCode(...new Uint8Array(p256dh)));
    const authBase64 = btoa(String.fromCharCode(...new Uint8Array(auth)));

    await api.post("/api/notifications/push/subscribe", {
      subscription: {
        endpoint: subscription.endpoint,
        keys: { p256dh: p256dhBase64, auth: authBase64 },
      },
      userAgent: navigator.userAgent,
      deviceName: getDeviceName(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    console.log("[Push/WebPrompt] Subscribed");
  } catch (err) {
    console.error("[Push/WebPrompt] Failed:", err);
  }
}

// Helper to generate a device name based on browser/OS
function getDeviceName(): string {
  if (isNative) {
    return `${nativePlatform === "ios" ? "iPhone" : "Android"} App`;
  }

  const ua = navigator.userAgent;

  // Detect OS
  let os = "Unknown";
  if (ua.includes("Windows")) os = "Windows";
  else if (ua.includes("Mac")) os = "macOS";
  else if (ua.includes("Linux")) os = "Linux";
  else if (ua.includes("Android")) os = "Android";
  else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

  // Detect browser
  let browser = "Browser";
  if (ua.includes("Chrome") && !ua.includes("Edg")) browser = "Chrome";
  else if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";
  else if (ua.includes("Edg")) browser = "Edge";

  return `${browser} on ${os}`;
}
